/**
 * Configuración de Frecuencias Temporales
 *
 * Define cómo se organizan y resetean los protocolos según su periodicidad.
 */

import type { Frequency, TabConfig } from '@/core/types';

/**
 * Configuración de cada frecuencia
 */
export const FREQUENCIES: Record<Frequency, TabConfig> = {
  daily: {
    id: 'daily',
    label: 'Diario',
    shortLabel: 'Hoy',
    icon: 'Calendar',
  },
  weekly: {
    id: 'weekly',
    label: 'Semanal',
    shortLabel: 'Semana',
    icon: 'CalendarDays',
  },
  monthly: {
    id: 'monthly',
    label: 'Mensual',
    shortLabel: 'Mes',
    icon: 'CalendarRange',
  },
  quarterly: {
    id: 'quarterly',
    label: 'Trimestral',
    shortLabel: 'Trim',
    icon: 'CalendarClock',
  },
  yearly: {
    id: 'yearly',
    label: 'Anual',
    shortLabel: 'Año',
    icon: 'CalendarCheck',
  },
} as const;

/**
 * Orden de las frecuencias
 */
export const FREQUENCY_ORDER: Frequency[] = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];

/**
 * Obtener el label de una frecuencia
 */
export function getFrequencyLabel(frequency: Frequency): string {
  return FREQUENCIES[frequency].label;
}

/**
 * Obtener la siguiente fecha de reset para una frecuencia
 */
export function getNextResetDate(frequency: Frequency, from: Date = new Date()): Date {
  const next = new Date(from);

  switch (frequency) {
    case 'daily':
      // Reset al inicio del próximo día
      next.setDate(next.getDate() + 1);
      next.setHours(0, 0, 0, 0);
      break;

    case 'weekly':
      // Reset al próximo lunes
      const daysUntilMonday = (8 - next.getDay()) % 7 || 7;
      next.setDate(next.getDate() + daysUntilMonday);
      next.setHours(0, 0, 0, 0);
      break;

    case 'monthly':
      // Reset al primer día del próximo mes
      next.setMonth(next.getMonth() + 1, 1);
      next.setHours(0, 0, 0, 0);
      break;

    case 'quarterly':
      // Reset al primer día del próximo trimestre
      const currentQuarter = Math.floor(next.getMonth() / 3);
      const nextQuarterMonth = (currentQuarter + 1) * 3;
      if (nextQuarterMonth >= 12) {
        next.setFullYear(next.getFullYear() + 1);
        next.setMonth(0, 1);
      } else {
        next.setMonth(nextQuarterMonth, 1);
      }
      next.setHours(0, 0, 0, 0);
      break;

    case 'yearly':
      // Reset al primer día del próximo año
      next.setFullYear(next.getFullYear() + 1);
      next.setMonth(0, 1);
      next.setHours(0, 0, 0, 0);
      break;
  }

  return next;
}

/**
 * Verificar si un protocolo debe resetearse
 */
export function shouldReset(
  frequency: Frequency,
  lastCompleted: string | undefined,
  now: Date = new Date()
): boolean {
  if (!lastCompleted) return false;

  const lastDate = new Date(lastCompleted);

  switch (frequency) {
    case 'daily':
      // Reset si es un día diferente
      return (
        lastDate.getDate() !== now.getDate() ||
        lastDate.getMonth() !== now.getMonth() ||
        lastDate.getFullYear() !== now.getFullYear()
      );

    case 'weekly':
      // Reset si es una semana diferente
      const getWeek = (d: Date) => {
        const firstDay = new Date(d.getFullYear(), 0, 1);
        return Math.ceil(
          ((d.getTime() - firstDay.getTime()) / 86400000 + firstDay.getDay() + 1) / 7
        );
      };
      return (
        getWeek(lastDate) !== getWeek(now) ||
        lastDate.getFullYear() !== now.getFullYear()
      );

    case 'monthly':
      // Reset si es un mes diferente
      return (
        lastDate.getMonth() !== now.getMonth() ||
        lastDate.getFullYear() !== now.getFullYear()
      );

    case 'quarterly':
      // Reset si es un trimestre diferente
      const getQuarter = (d: Date) => Math.floor(d.getMonth() / 3);
      return (
        getQuarter(lastDate) !== getQuarter(now) ||
        lastDate.getFullYear() !== now.getFullYear()
      );

    case 'yearly':
      // Reset si es un año diferente
      return lastDate.getFullYear() !== now.getFullYear();

    default:
      return false;
  }
}
